import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Flex, Text, useColorMode } from "@chakra-ui/react";
import { FiExternalLink } from "react-icons/fi";

export const InteractiveConnectorsPlayground: React.FC<{
  // The playground state is the URL hash that contains the current state of the playground
  state: string;
  disableEmbed?: boolean;
}> = ({ state, disableEmbed }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframe, setIframe] = useState<ReactNode | null>(null);
  const { colorMode } = useColorMode();
  useEffect(() => {
    if (!iframeRef.current) return;
    if (!colorMode) return;
    // Send theme change message to iframe
    iframeRef.current.contentWindow?.postMessage(
      { type: "theme", theme: colorMode },
      "*"
    );
  }, [colorMode]);
  const url = useMemo(() => {
    if (!colorMode) return;
    const url = new URL(
      "https://www.apollographql.com/connectors-mapping-playground"
    );
    if (!disableEmbed) url.searchParams.set("embed", "");
    url.searchParams.set("theme", colorMode);
    url.hash = state;
    return url.href;
  }, [colorMode, disableEmbed, state]);
  useEffect(() => {
    setIframe(
      <iframe
        ref={iframeRef}
        src={url}
        title="Apollo Connectors Mapping Playground"
        width="100%"
        height="500px"
        allow="clipboard-write"
      />
    );
    // Intentionally left empty to avoid updating the iframe on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      border="1px"
      borderColor={{ base: "gray.300", _dark: "navy.400" }}
      borderRadius="md"
      w="full"
      display="grid"
      gridTemplateRows={{ base: "1fr", md: "auto 500px" }}
      overflow="hidden"
    >
      <Flex
        borderBottom={{ base: "none", md: "1px" }}
        borderColor={{ base: "transparent", md: "gray.300", _dark: "navy.400" }}
        px={4}
        py={2}
        flexDirection={{ base: "column", md: "row" }}
        alignItems="center"
      >
        <Text fontWeight="semibold" fontSize="md">
          Connectors Mapping Playground
        </Text>
        <Text
          display={{ base: "block", md: "none" }}
          fontSize="sm"
          textAlign="center"
          mb={1}
        >
          Best viewed on a larger screen
        </Text>
        <Button
          variant="ghost"
          size="sm"
          ml={{ base: 0, md: "auto" }}
          color={{ base: "gray.500", _dark: "navy.100" }}
          _hover={{
            color: { base: "gray.700", _dark: "navy.200" },
          }}
          aria-label="Open in new tab"
          title="Open in new tab"
          onClick={() => {
            let currentUrl = url;
            try {
              currentUrl = new URL(
                iframeRef.current?.contentWindow?.location.href ??
                  url ??
                  "https://www.apollographql.com/connectors-mapping-playground"
              ).href;
            } catch (e) {
              currentUrl =
                url ??
                "https://www.apollographql.com/connectors-mapping-playground";
              // Ignore error
            }
            const newWindow = window.open(currentUrl, "_blank");
            if (newWindow) {
              newWindow.focus();
            }
          }}
          leftIcon={<FiExternalLink />}
        >
          Open in new tab
        </Button>
      </Flex>
      <Box display={{ base: "none", md: "block" }}>{iframe ?? <div />}</Box>
    </Box>
  );
};
